import { useState, type FormEvent } from "react";
import emailjs from "@emailjs/browser";
import { useTranslation } from "react-i18next";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import { useSiteInfo } from "@/hooks/useSiteInfo";

interface InquiryType {
  value: string;
  label: string;
}

export default function ContactPage() {
  const { t } = useTranslation();
  const site = useSiteInfo();
  const inquiryTypes = t("contact.page.form.inquiryTypes", { returnObjects: true }) as unknown as InquiryType[];

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    const formData = new FormData(form);
    if (honeypot) formData.delete("phone_alt");

    try {
      await fetch("https://readdy.ai/api/form/d8tjdj4al24muhn2r84g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
    } catch {
      // silently handle
    }

    emailjs.sendForm("service_ml5rlis", "template_hzrsu2e", form).catch(() => {});

    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <SiteLayout navVariant="solid">
        <PageBanner
          title={t("contact.page.title")}
          subtitle={t("contact.page.submittedSubtitle")}
          breadcrumb={[{ label: t("contact.page.title") }]}
          bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20office%20interior%20with%20warm%20lighting%2C%20professional%20business%20environment%2C%20organized%20workspace%2C%20corporate%20atmosphere&width=1600&height=680&seq=krubber-contact-banner&orientation=landscape"
        />
        <section className="py-20 bg-background-50">
          <div className="max-w-[640px] mx-auto px-6 text-center">
            <span className="w-20 h-20 mx-auto rounded-2xl bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-primary-600 text-3xl" />
            </span>
            <h2 className="mt-6 text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
              {t("contact.page.submittedTitle")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              {t("contact.page.submittedDesc")}{" "}
              <strong className="text-foreground-950">{site.tel}</strong>
            </p>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title={t("contact.page.title")}
        subtitle={t("contact.page.bannerSubtitle")}
        breadcrumb={[{ label: t("contact.page.title") }]}
        bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20office%20interior%20with%20warm%20lighting%2C%20professional%20business%20environment%2C%20organized%20workspace%2C%20corporate%20atmosphere&width=1600&height=680&seq=krubber-contact-banner&orientation=landscape"
      />
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            <div className="lg:w-[480px] flex-shrink-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                {t("contact.page.contactInfoTitle")}
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                {t("contact.page.contactInfoDesc")}
              </p>
              <div className="mt-8 flex flex-col gap-4">
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-600 text-lg" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">{t("contact.page.tel")}</p>
                    <a href={site.telLink} className="text-lg font-heading font-extrabold text-foreground-950 hover:text-primary-600 transition-colors cursor-pointer">
                      {site.tel}
                    </a>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-printer-line text-primary-600 text-lg" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">{t("contact.page.fax")}</p>
                    <p className="text-lg font-heading font-extrabold text-foreground-950">{site.fax}</p>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600 text-lg" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">{t("contact.page.email")}</p>
                    <a href={site.emailLink} className="text-lg font-heading font-extrabold text-foreground-950 hover:text-primary-600 transition-colors cursor-pointer">
                      {site.email}
                    </a>
                  </div>
                </div>
                <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex items-center gap-4">
                  <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-2-line text-primary-600 text-lg" />
                  </span>
                  <div>
                    <p className="text-xs font-bold text-foreground-500 uppercase tracking-wide">{t("contact.page.address")}</p>
                    <p className="text-sm font-semibold text-foreground-900 leading-relaxed">{site.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                {t("contact.page.onlineTitle")}
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                {t("contact.page.onlineDesc")}
              </p>
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-8 flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="company" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("contact.page.form.company")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("contact.page.form.companyPlaceholder")}
                    />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("contact.page.form.name")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("contact.page.form.namePlaceholder")}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("contact.page.form.phone")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("contact.page.form.phonePlaceholder")}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("contact.page.form.email")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("contact.page.form.emailPlaceholder")}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="inquiry_type" className="block text-xs font-bold text-foreground-600 mb-2">
                    {t("contact.page.form.inquiryType")}
                  </label>
                  <select
                    id="inquiry_type"
                    name="inquiry_type"
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  >
                    <option value="">{t("contact.page.form.inquiryTypePlaceholder")}</option>
                    {inquiryTypes.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs font-bold text-foreground-600 mb-2">
                    {t("contact.page.form.message")} <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder={t("contact.page.form.messagePlaceholder")}
                  />
                  <p className="mt-1 text-xs text-foreground-400">{t("contact.page.form.messageHint")}</p>
                </div>
                <div>
                  <label htmlFor="attachment" className="block text-xs font-bold text-foreground-600 mb-2">
                    {t("contact.page.form.attachment")}
                  </label>
                  <input
                    type="file"
                    id="attachment"
                    name="attachment"
                    className="w-full text-sm text-foreground-600 file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200 file:cursor-pointer"
                  />
                  <p className="mt-1 text-xs text-foreground-400">
                    {t("contact.page.form.attachmentHint")}
                  </p>
                </div>

                <div className="flex items-start gap-3 mt-2">
                  <input
                    type="checkbox"
                    id="privacy_consent"
                    name="privacy_consent"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="privacy_consent" className="text-xs text-foreground-600 leading-relaxed">
                    {t("contact.page.form.consent")} <span className="text-accent-500">*</span>
                  </label>
                </div>

                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hp-shield"
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-4 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin" />
                      {t("contact.page.form.submitting")}
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      {t("contact.page.form.submit")}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}